import React, { FC, useLayoutEffect, useRef, useState } from 'react';

const THICKNESS = 30; // width of sectors line
const GAP = 3; // space between sectors
const LABEL_OFFSET = 22; // px from ring outer edge to label center
const PILL_W = 36;
const PILL_H = 22;
const PILL_RX = 11;
const FONT_SIZE = 12;

interface IDonutSlice {
  salesPercent: number;
  color: string;
}

interface IDonutChartProps {
  items: IDonutSlice[];
  hoveredIndex?: number | null;
  onHoverChange?: (index: number | null) => void;
}

export const DonutChart: FC<IDonutChartProps> = ({ items, hoveredIndex = null, onHoverChange }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(200);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(([entry]) => setSize(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cx = size / 2;
  const cy = size / 2;
  // r is computed so that labels (pill edges) stay within the SVG bounds
  const r = Math.max(10, cx - THICKNESS / 2 - LABEL_OFFSET - PILL_W / 2 - 4);
  const labelR = r + THICKNESS / 2 + LABEL_OFFSET;
  const circumference = 2 * Math.PI * r;
  const total = items.reduce((s, i) => s + i.salesPercent, 0) || 100;

  // Pre-compute all slice geometry in one pass
  let cumulativePct = 0;
  const slices = items.map((item) => {
    const pct = (item.salesPercent / total) * 100;
    const dashLength = Math.max(1, (pct / 100) * circumference - GAP);
    const dashOffset = -(cumulativePct / 100) * circumference;
    const midFraction = (cumulativePct + pct / 2) / 100;
    const midAngle = midFraction * 2 * Math.PI - Math.PI / 2;
    const lx = cx + labelR * Math.cos(midAngle);
    const ly = cy + labelR * Math.sin(midAngle);
    cumulativePct += pct;
    return { item, dashLength, dashOffset, lx, ly };
  });

  return (
    <div ref={wrapRef} style={{ width: '100%', aspectRatio: '1 / 1' }}>
      <svg width={size} height={size}>
        {slices.map(({ item, dashLength, dashOffset }, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={item.color}
            strokeWidth={THICKNESS}
            strokeDasharray={`${dashLength} ${circumference - dashLength}`}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
            opacity={hoveredIndex === null || hoveredIndex === i ? 1 : 0.4}
            style={{ transition: 'opacity 0.15s ease' }}
            onMouseEnter={() => onHoverChange?.(i)}
            onMouseLeave={() => onHoverChange?.(null)}
          />
        ))}
        {slices.map(({ item, lx, ly }, i) => (
          <g
            key={i}
            className={`ms-products__label ${hoveredIndex === i ? 'ms-products__label--active' : ''}`}
            opacity={hoveredIndex === null || hoveredIndex === i ? 1 : 0.4}
            style={{ transition: 'opacity 0.15s ease' }}
            onMouseEnter={() => onHoverChange?.(i)}
            onMouseLeave={() => onHoverChange?.(null)}
          >
            <rect x={lx - PILL_W / 2} y={ly - PILL_H / 2} width={PILL_W} height={PILL_H} rx={PILL_RX} />
            <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize={FONT_SIZE}>
              {item.salesPercent}%
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};
